import * as React from "react";
import gql from "graphql-tag";
import OperatingTimesRow from "./OperatingTimesRow";
import withCreateUpdateQueries from "../../generic/withCreateUpdateQueries";
import withOpenSnackbar, {
  OpenSnackbarFn,
} from "../../generic/snackbar/withOpenSnackbar";
import getOptimisticID from "../../../../helpers/getOptimisticID";
import { OperatingTimesDayOperationHours as OperationHours } from "./__generated__/OperatingTimesDayOperationHours";
import { OperatingTimesDayCreateOperationHoursVariables } from "./__generated__/OperatingTimesDayCreateOperationHours";
import { OperatingTimesDayUpdateOperationHoursVariables } from "./__generated__/OperatingTimesDayUpdateOperationHours";
import { format24Hour, parse24Hour } from "../../../../helpers/formatTime";
import { FieldStatus } from "../../generic/form/StatusIcon";
import { compose } from "recompose";
import { debounce } from "lodash";
import { SnackbarType } from "../../../../__generated__/globalTypes";

const SAVE_DEBOUNCE_TIME = 100;

export const operatingTimesDayFragments = {
  operationHours: gql`
    fragment OperatingTimesDayOperationHours on OperationHours {
      id
      dayOfTheWeek
      openingTime
      closingTime
    }
  `,
};

export const CREATE_OPERATION_HOURS = gql`
  mutation OperatingTimesDayCreateOperationHours(
    $warehouseID: String!
    $dayOfTheWeek: String!
    $openingTime: String!
    $closingTime: String!
  ) {
    createOperationHours(
      values: {
        dayOfTheWeek: $dayOfTheWeek
        openingTime: $openingTime
        closingTime: $closingTime
      }
      warehouse: { id: $warehouseID }
    ) {
      ...OperatingTimesDayOperationHours
    }
  }
  ${operatingTimesDayFragments.operationHours}
`;

export const UPDATE_OPERATION_HOURS = gql`
  mutation OperatingTimesDayUpdateOperationHours(
    $id: String!
    $openingTime: String!
    $closingTime: String!
  ) {
    updateOperationHours(
      id: $id
      values: { openingTime: $openingTime, closingTime: $closingTime }
    ) {
      ...OperatingTimesDayOperationHours
    }
  }
  ${operatingTimesDayFragments.operationHours}
`;

export const DELETE_OPERATION_HOURS = gql`
  mutation OperatingTimesDayDeleteOperationHours($id: String!) {
    deleteOperationHours(id: $id) {
      id
    }
  }
`;

export type EnhancedProps = {
  dayOfTheWeek: string;
  operationHours?: OperationHours;
  organisationID: string;
  warehouseID: string;
};

type Props = EnhancedProps & {
  createMutation: (
    arg0: OperatingTimesDayCreateOperationHoursVariables
  ) => Promise<void>;
  deleteMutation: (id: string) => Promise<void>;
  openSnackbar: OpenSnackbarFn;
  updateMutation: (
    arg0: OperatingTimesDayUpdateOperationHoursVariables
  ) => Promise<void>;
};

type State = {
  closingTime: string;
  open: boolean;
  openingTime: string;
  status: FieldStatus;
};

/**
 * Component to manage the state and updates to a day's operation hours.
 */
export class OperatingTimesDay extends React.PureComponent<Props, State> {
  saveChanges: () => void;

  constructor(props: Props) {
    super(props);

    // @ts-ignore
    this.state = {
      ...this.getInitialState(),
      status: `normal`,
    };
    this.saveChanges = debounce(
      this.saveChangesImmediately.bind(this),
      SAVE_DEBOUNCE_TIME
    );
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    const { operationHours } = this.props;
    const { operationHours: prevOperationHours } = prevProps;
    const { open } = this.state;
    const { open: prevOpen } = prevState;

    // Reinitialise state if operationHours is set/unset or the ID changes
    if (
      operationHours !== prevOperationHours &&
      (operationHours == null ||
        prevOperationHours == null ||
        operationHours.id !== prevOperationHours.id)
    ) {
      // @ts-ignore
      this.setState(this.getInitialState());
    }

    // Save when open is changed
    if (open !== prevOpen) {
      this.saveChanges();
    }
  }

  /**
   * @return {State} The initial state depending on operationHours prop.
   */
  getInitialState(): Partial<State> {
    const { operationHours } = this.props;
    if (operationHours) {
      return {
        closingTime: format24Hour(operationHours.closingTime) || ``,
        open: true,
        openingTime: format24Hour(operationHours.openingTime) || ``,
      };
    } else {
      return {
        closingTime: ``,
        open: false,
        openingTime: ``,
      };
    }
  }

  handleOpenChange(open: boolean) {
    this.setState({
      open,
      status: `normal`,
    });
  }

  handleTimesChange(openingTime: string, closingTime: string) {
    this.setState({
      closingTime,
      openingTime,
      status: `normal`,
    });
  }

  /**
   * Perform required mutation to save current state, if it is valid.
   * @return {void}
   */
  async saveChangesImmediately() {
    const {
      createMutation,
      dayOfTheWeek,
      deleteMutation,
      openSnackbar,
      operationHours,
      updateMutation,
      warehouseID,
    } = this.props;
    const { closingTime, open, openingTime } = this.state;

    const closingTimeParsed = parse24Hour(closingTime);
    const openingTimeParsed = parse24Hour(openingTime);

    if (closingTimeParsed == null || openingTimeParsed == null) {
      return;
    }

    // Determine which mutation to run
    let performMutation, mutationVariables;

    if (operationHours && !open) {
      // Remove the existing operation hours
      performMutation = deleteMutation;
      mutationVariables = operationHours.id as string;
    } else if (open) {
      if (!operationHours) {
        // Create operation hours
        performMutation = createMutation;
        mutationVariables = {
          closingTime: closingTimeParsed,
          dayOfTheWeek,
          openingTime: openingTimeParsed,
          warehouseID,
        } as OperatingTimesDayCreateOperationHoursVariables;
      } else if (
        operationHours.openingTime !== openingTimeParsed ||
        operationHours.closingTime !== closingTimeParsed
      ) {
        // Update hours of existing OperationHours
        performMutation = updateMutation;
        mutationVariables = {
          closingTime: closingTimeParsed,
          id: operationHours.id,
          openingTime: openingTimeParsed,
        } as OperatingTimesDayUpdateOperationHoursVariables;
      }
    }

    if (performMutation) {
      this.setState({
        status: `saving`,
      });
      try {
        await performMutation(mutationVariables as any);
        openSnackbar(
          SnackbarType.success,
          `Successfully updated operating times for ${dayOfTheWeek}`
        );
        this.setState({
          status: `saved`,
        });
      } catch (error) {
        openSnackbar(
          SnackbarType.error,
          `Failed to update operating times for ${dayOfTheWeek}`
        );
        this.setState({
          status: `failed`,
        });
      }
    }
  }

  render() {
    const { dayOfTheWeek } = this.props;
    const { closingTime, open, openingTime, status } = this.state;

    return (
      <OperatingTimesRow
        closingTime={closingTime}
        dayOfTheWeek={dayOfTheWeek}
        disabled={status === `saving`}
        open={open}
        openingTime={openingTime}
        status={status}
        onOpenChange={this.handleOpenChange.bind(this)}
        onTimesBlur={() => this.saveChanges()}
        onTimesChange={this.handleTimesChange.bind(this)}
      />
    );
  }
}

const enhancer = compose<any, EnhancedProps>(
  withCreateUpdateQueries({
    createItemAccessPath: `createOperationHours`,
    createMutation: CREATE_OPERATION_HOURS,
    createOptimisticResponse: ({ dayOfTheWeek, openingTime, closingTime }) => ({
      createOperationHours: {
        __typename: `OperationHours`,
        closingTime,
        dayOfTheWeek,
        id: getOptimisticID(),
        openingTime,
      },
    }),
    deleteMutation: DELETE_OPERATION_HOURS,
    deleteOptimisticResponse: (id) => ({
      deleteOperationHours: {
        __typename: `OperationHours`,
        id,
      },
    }),
    listAccessPath: `getSelf.organisation.warehouse.operationHoursList`,
    listQuery: gql`
      query OperatingTimesDayGetWarehouse(
        $organisationID: String!
        $warehouseID: String!
      ) {
        getSelf {
          id
          organisation(id: $organisationID) {
            id
            warehouse(id: $warehouseID) {
              id
              operationHoursList {
                ...OperatingTimesDayOperationHours
              }
            }
          }
        }
      }
      ${operatingTimesDayFragments.operationHours}
    `,
    listQueryVariables: ({ organisationID, warehouseID }) => ({
      organisationID,
      warehouseID,
    }),
    updateMutation: UPDATE_OPERATION_HOURS,
    updateOptimisticResponse: (
      { id, openingTime, closingTime },
      { dayOfTheWeek }: Props
    ) => ({
      updateOperationHours: {
        __typename: `OperationHours`,
        closingTime,
        dayOfTheWeek,
        id,
        openingTime,
      },
    }),
  }),
  withOpenSnackbar
);

export default enhancer(OperatingTimesDay);
